import i18n from '@/i18n';
import { listingTemplateAPI, fundingAuthAPI, templateGroupAPI, getIpeWorkspaceMode } from '@/utils/api';
import { filterTableListingTemplates } from '@/utils/listingTemplatePlatformUtils';
import {
  annotateImageTemplateScopes,
  filterImageTemplatesByUserTeamAccess,
} from '@/pages/ipe/workbench/utils/imageTemplateAccess';
import { isTeamSpaceEntryAvailable, normalizeWorkspaceMode } from '@/pages/ipe/workbench/utils/accountCenterUtils';
import { filterByListingWorkspaceScope } from '@/pages/ipe/workbench/utils/listingWorkspaceMeta';

/** 导出弹窗「未分组」哨兵值 */
export const LISTING_EXPORT_GROUP_UNGROUPED = '__ungrouped__';

export const LISTING_EXPORT_PAGE_SIZE_OPTIONS = [50, 100, 200];
export const LISTING_EXPORT_DEFAULT_PAGE_SIZE = 50;

/**
 * @returns {Promise<{
 *   authUserId: string|null,
 *   authDisplayName: string,
 *   userNo: string,
 *   workspaceMode: 'personal'|'team',
 *   canAccessTeam: boolean,
 *   teamId: string|null,
 *   teamNo: string,
 *   creatorOptions: Array<{ value: string, label: string }>,
 * }>}
 */
export async function resolveTableListingTemplatesAccess() {
  let me = null;
  let canAccessTeam = false;
  let teamId = null;
  let teamNo = '';
  const workspaceMode = normalizeWorkspaceMode(getIpeWorkspaceMode());

  try {
    me = await fundingAuthAPI.getMe();
  } catch {
    me = null;
  }
  const user = me?.user || me?.data || me || null;
  const authUserId = user?.id ?? user?.userId ?? null;
  const userNo = user?.userNo ? String(user.userNo).trim() : '';
  const nickname = (user?.nickname && String(user.nickname).trim()) || '';
  const phone = user?.phone ? String(user.phone) : '';
  const authDisplayName = nickname
    || (phone.length >= 7 ? `${phone.slice(0, 3)}****${phone.slice(-4)}` : '我');

  try {
    const center = await fundingAuthAPI.getAccountCenter();
    canAccessTeam = isTeamSpaceEntryAvailable(center);
  } catch {
    canAccessTeam = false;
  }

  // 团队工作区才拉团队编号/成员；个人模式只走个人数据源
  const useTeamScope = workspaceMode === 'team' && canAccessTeam;
  if (useTeamScope) {
    try {
      const overview = await fundingAuthAPI.getTeamOverview();
      const team = overview?.team && typeof overview.team === 'object' ? overview.team : overview;
      teamId = team?.id ?? team?.teamId ?? null;
      teamNo = team?.teamNo ?? team?.team_no ?? '';
    } catch {
      teamId = null;
      teamNo = '';
    }
  }

  const allOption = { value: '', label: i18n.t('workbench:listingTemplatePicker.groupAll') };
  let creatorOptions = [allOption];
  if (useTeamScope) {
    try {
      const overview = await fundingAuthAPI.getTeamOverview();
      const members = Array.isArray(overview?.members) ? overview.members : [];
      creatorOptions = [
        allOption,
        ...members.map((member) => ({
          value: String(member.userId),
          label: member.nickname || member.phoneMasked || `成员 ${member.userId}`,
        })),
      ];
    } catch {
      creatorOptions = [allOption];
    }
  }

  return {
    authUserId,
    authDisplayName,
    userNo,
    workspaceMode: useTeamScope ? 'team' : 'personal',
    canAccessTeam,
    teamId: useTeamScope ? teamId : null,
    teamNo: useTeamScope ? String(teamNo || '').trim() : '',
    creatorOptions,
  };
}

/**
 * 加载表格模板分组名（含 DB 分组配置），不拉取全量模板。
 * @returns {Promise<string[]>}
 */
export async function loadTableListingTemplateGroupNames() {
  try {
    const groupsRes = await templateGroupAPI.getAll();
    const dbGroups = groupsRes?.groups?.tableTemplates;
    if (Array.isArray(dbGroups)) {
      return Array.from(new Set(dbGroups.map((g) => String(g || '').trim()).filter(Boolean)));
    }
  } catch {
    // ignore
  }
  return [];
}

function pickTemplateOwnerId(template) {
  return template?.ownerId
    ?? template?.owner_id
    ?? template?.userId
    ?? template?.user_id
    ?? template?.createdByUserId
    ?? template?.ownerUserId
    ?? null;
}

function matchesExportFilters(template, filters = {}, authUserId = null) {
  const {
    name = '',
    platform = '',
    creatorUserId = '',
    group = '',
  } = filters;

  const matchesName = !String(name || '').trim()
    || (template.name && template.name.toLowerCase().includes(String(name).toLowerCase().trim()));
  const matchesPlatform = !platform || template.platform === platform;
  const matchesCreator = !creatorUserId
    || (() => {
      const ownerId = pickTemplateOwnerId(template);
      if (ownerId != null && ownerId !== '') {
        return String(ownerId) === String(creatorUserId);
      }
      return authUserId != null && String(creatorUserId) === String(authUserId);
    })();
  const matchesGroup = !group
    || (group === LISTING_EXPORT_GROUP_UNGROUPED
      ? !template.group
      : template.group === group);

  return matchesName && matchesPlatform && matchesCreator && matchesGroup;
}

/**
 * 两层过滤（UX / 防御性）：
 * 1) ACL：个人仅本人、团队需 canAccessTeam 且团队匹配（官方可读）
 * 2) 当前工作区：个人模式只出个人源，团队模式只出团队源
 * 安全边界已在服务端 workspace 分页；此处保留薄过滤以防旧缓存/脏数据。
 */
function applyScopedFilters(rawList, access) {
  const tableList = filterTableListingTemplates(
    Array.isArray(rawList) ? rawList : Object.values(rawList || {}),
  );
  const aclFiltered = filterImageTemplatesByUserTeamAccess(tableList, {
    authUserId: access?.authUserId ?? null,
    userNo: access?.userNo || '',
    canAccessTeam: Boolean(access?.canAccessTeam),
    teamId: access?.teamId ?? null,
    teamNo: access?.teamNo || '',
  });

  const workspaceMode = normalizeWorkspaceMode(access?.workspaceMode || getIpeWorkspaceMode());
  const effectiveMode = workspaceMode === 'team' && access?.canAccessTeam ? 'team' : 'personal';
  const listingScope = {
    workspaceMode: effectiveMode,
    userNo: access?.userNo || '',
    teamNo: effectiveMode === 'team' ? (access?.teamNo || '') : '',
    teamId: effectiveMode === 'team' ? (access?.teamId ?? null) : null,
    scopeNo: effectiveMode === 'team' ? (access?.teamNo || '') : (access?.userNo || ''),
    authUserId: access?.authUserId ?? null,
    ready: effectiveMode !== 'team' || access?.teamId != null,
  };

  return annotateImageTemplateScopes(
    filterByListingWorkspaceScope(aclFiltered, listingScope),
  );
}

/**
 * 服务端工作区隔离分页：按 server page 拉取，仅做平台/分组/名称等 UX 二次过滤。
 * cursor 使用 serverPage（从 1 起）；不再依赖「全库 over-fetch + 客户端凑页」做租户隔离。
 *
 * @param {{
 *   pageSize?: number,
 *   cursor?: { serverPage?: number } | null,
 *   filters?: object,
 *   access?: object,
 * }} [options]
 */
export async function loadTableListingTemplatesCursorPage({
  pageSize = LISTING_EXPORT_DEFAULT_PAGE_SIZE,
  cursor = null,
  filters = {},
  access: accessInput = null,
} = {}) {
  const access = accessInput || await resolveTableListingTemplatesAccess();
  const limit = Math.min(200, Math.max(1, Number(pageSize) || LISTING_EXPORT_DEFAULT_PAGE_SIZE));
  const serverPage = Math.max(1, Number(cursor?.serverPage) || 1);

  const query = {
    page: serverPage,
    pageSize: limit,
  };
  const creatorUserId = filters?.creatorUserId;
  if (creatorUserId != null && String(creatorUserId).trim() !== '') {
    const asNum = Number(creatorUserId);
    if (Number.isFinite(asNum)) {
      query.ownerUserId = asNum;
    }
  }

  const res = await listingTemplateAPI.getAll(query);
  const rawBatch = Array.isArray(res?.templates) ? res.templates : [];
  const serverTotalPages = Math.max(1, Number(res?.totalPages) || 1);
  const serverHasMore = res?.hasMore === true
    || (res?.hasMore !== false && serverPage < serverTotalPages);

  const scoped = applyScopedFilters(rawBatch, access);
  const discoveredGroups = new Set();
  let ungroupedCountHint = 0;
  const templates = [];
  for (const item of scoped) {
    if (item.group) discoveredGroups.add(item.group);
    else ungroupedCountHint += 1;
    if (!matchesExportFilters(item, filters, access.authUserId)) {
      continue;
    }
    templates.push(item);
  }

  const hasMore = serverHasMore;
  const nextCursor = hasMore
    ? { serverPage: serverPage + 1 }
    : { serverPage };

  return {
    templates,
    nextCursor,
    hasMore,
    access,
    discoveredGroups: Array.from(discoveredGroups),
    ungroupedCountHint,
    page: serverPage,
    pageSize: limit,
    total: Number(res?.total) || templates.length,
    totalPages: serverTotalPages,
  };
}

/**
 * 详情/工作流「表格模板」选择：按当前工作区（个人/团队）+ ACL 过滤，避免跨租户泄漏。
 * @returns {Promise<{ templates: object[], canAccessTeam: boolean, workspaceMode: string, groups: string[] }>}
 */
export async function loadTableListingTemplatesScoped() {
  const access = await resolveTableListingTemplatesAccess();
  const { templates } = await listingTemplateAPI.getAll();
  const annotated = applyScopedFilters(templates, access);
  const groups = Array.from(new Set(annotated.map((t) => t.group).filter(Boolean)));

  return {
    templates: annotated,
    canAccessTeam: access.canAccessTeam,
    workspaceMode: access.workspaceMode,
    groups,
  };
}
